import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useGeolocation } from '../hooks/useGeolocation';
import { userLocationAtom, locationAccuracyAtom, isHighAccuracyAtom, locationErrorAtom, locationPermissionAtom } from '../atoms/location';
import { mapStateAtom } from '../atoms/map';
import { isInHaiti } from '../utils/locationUtils';

interface LocationContextValue {
  requestHighAccuracy: () => void;
  refreshLocation: () => void;
  requestLocationPermission: () => void;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const geolocation = useGeolocation();
  const setUserLocation = useSetAtom(userLocationAtom);
  const setAccuracy = useSetAtom(locationAccuracyAtom);
  const setIsHighAccuracy = useSetAtom(isHighAccuracyAtom);
  const setError = useSetAtom(locationErrorAtom);
  const setPermissionStatus = useSetAtom(locationPermissionAtom);
  const [mapState, setMapState] = useAtom(mapStateAtom);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);
  const [hasHandledNonHaitiLocation, setHasHandledNonHaitiLocation] = useState(false);

  // Effect for handling location updates
  useEffect(() => {
    if (geolocation.coordinates) {
      // Check if user is in Haiti
      const userIsInHaiti = isInHaiti(geolocation.coordinates.lat, geolocation.coordinates.lng);
      
      // Always update the user's actual location for other features
      setUserLocation(geolocation.coordinates);
      setAccuracy(geolocation.accuracy);
      setIsHighAccuracy(geolocation.accuracy ? geolocation.accuracy < 20 : false);
      
      // If user is not in Haiti and we haven't handled this yet
      if (!userIsInHaiti && !hasHandledNonHaitiLocation) {
        // Set the map to default Haiti center
        setMapState({
          center: [18.533333, -72.333333], // Haiti default center
          zoom: 8, // Zoomed out to show more of Haiti
          hasSetInitialLocation: true,
          shouldForceCenter: true // Force center to Haiti
        });
        
        // Mark that we've handled this case to prevent repeated recentering
        setHasHandledNonHaitiLocation(true);
      }
      // If this is the first time we get coordinates AND permission was just granted AND user is in Haiti
      else if (!mapState.hasSetInitialLocation && geolocation.permissionNewlyGranted && userIsInHaiti) {
        setMapState({
          center: [geolocation.coordinates.lat, geolocation.coordinates.lng],
          zoom: 16,
          hasSetInitialLocation: true,
          shouldForceCenter: true
        });
      }
    }
    
    if (geolocation.error) {
      setError(geolocation.error);
    }
    
    if (geolocation.permissionStatus) {
      setPermissionStatus(geolocation.permissionStatus);
    }
  }, [
    geolocation.coordinates,
    geolocation.accuracy,
    geolocation.error,
    geolocation.permissionStatus,
    geolocation.permissionNewlyGranted,
    setUserLocation,
    setAccuracy,
    setIsHighAccuracy,
    setError,
    setPermissionStatus,
    mapState,
    setMapState,
    hasHandledNonHaitiLocation,
    setHasHandledNonHaitiLocation
  ]);
  
  // Auto-request location permission on mount if not already requested
  useEffect(() => {
    if (!hasRequestedPermission) {
      // Request location permission immediately when the app loads
      geolocation.refresh?.();
      setHasRequestedPermission(true);
    }
  }, [hasRequestedPermission, geolocation]);

  // Add handling for permission denial
  useEffect(() => {
    // When location is denied or unavailable
    if (geolocation.error || geolocation.permissionStatus === 'denied') {
      // Mark initial location as set even if it's just the default location
      if (!mapState.hasSetInitialLocation) {
        setMapState(prev => ({
          ...prev,
          hasSetInitialLocation: true,
          // Use the default Haiti coordinates
          center: [18.533333, -72.333333],
          zoom: 8  // Zoom out to show more of the area
        }));
      }
    }
  }, [geolocation.error, geolocation.permissionStatus, mapState.hasSetInitialLocation, setMapState]);

  const value = {
    requestHighAccuracy: () => {
      geolocation.requestHighAccuracy?.();
    },
    refreshLocation: () => {
      geolocation.refresh?.();
    },
    requestLocationPermission: () => {
      geolocation.refresh?.();
    }
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationState() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationState must be used within a LocationProvider');
  }
  return context;
}
